import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page, useCopilotRecord } from '@/shell/AppShell'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeAIButton } from '@/shell/ForgeAIButton'
import { HierarchyTrail } from '@/components/forge/HierarchyTrail'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { PlainBadge, SeverityBadge, hueForSeverity, statusLabel } from '@/components/forge/StatusBadge'
import { Eyebrow, Num, Serial } from '@/components/forge/primitives'
import {
  useLogbookAccess,
  useLogbookEntry,
  useLogbookEntryStream,
  useUpdateLogbookEntry,
} from '@/features/logbook/queries'
import { usePermission } from '@/features/auth/permissions'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { ERROR_CODES } from '@/services/apiError'
import { formatDateTime, formatDay } from '@/lib/format'
import { STRIPE_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'
import { LogbookFormDialog } from './LogbookFormDialog'

/**
 * One log book entry, read. LIVE — `GET /v1/logbook/{id}`, kept live by
 * `/logbook/{id}/stream` for as long as it is open, so an edit made elsewhere
 * (or by the copilot) lands here without a reload.
 *
 * Its own route, `/app/logbook/:entryId`, rather than a dialog over the feed:
 * an entry is what an investigation cites, and a citation needs an address.
 *
 * ── Edit, and no Delete ──────────────────────────────────────────────────
 * `PUT` is offered through the same dialog that writes an entry. Every reader
 * sees it: `LogBookEntryOut` carries no `created_by`, so this client cannot
 * tell which entries a `logbook.record_entries` holder may change, and the
 * server's 403 is turned into a sentence that says why. `DELETE` is still not
 * offered — see `useLogbookAccess`. Recorded in BACKEND-GAPS.md §9.
 *
 * ── Still no byline ──────────────────────────────────────────────────────
 * For the same reason. The detail is where a reader most wants the author, and
 * the API does not serve one.
 */
export function LogbookEntryDetailPage() {
  const { t } = useTranslation(['app', 'common'])
  const { entryId } = useParams()
  const access = useLogbookAccess()
  const entry = useLogbookEntry(entryId, { enabled: access.granted })
  useLogbookEntryStream(entryId, { enabled: entry.isSuccess })
  // `#LOG-26` in the assistant's context chip.
  useCopilotRecord('logbook', entry.data?.id, entry.data?.serialNumber)

  useDocumentTitle(`${entry.data?.serialNumber ?? t('logbook.detail.title')} · ${t('common:brand.name')}`)

  const { toast } = useToast()
  const update = useUpdateLogbookEntry()
  const [editing, setEditing] = useState(false)
  const canPickAnomaly = usePermission('anomalies.manage').granted
  const canPickWorkOrder = usePermission('work_orders.manage').granted
  const canReadSensors = usePermission('sensors.manage_sensors').granted

  const closeForm = () => {
    setEditing(false)
    update.reset()
  }

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ForgeButton asChild>
          <Link to="/app/logbook">
            {/* A direction, not a glyph — mirrored under `dir="rtl"`. */}
            <Icon name="right" size="xs" className="rotate-180 rtl:rotate-0" />
            {t('logbook.detail.back')}
          </Link>
        </ForgeButton>

        <div className="flex-1" />

        {access.granted && entry.isSuccess ? (
          <>
            <ForgeAIButton action="updateLogbookEntry" recordId={entry.data.id} recordLabel={entry.data.serialNumber} />
            <ForgeButton onClick={() => setEditing(true)} disabled={editing}>
              {t('common:actions.edit')}
            </ForgeButton>
          </>
        ) : null}
      </div>

      {access.isPending ? (
        <SkeletonRows count={4} />
      ) : !access.granted ? (
        <EmptyState icon="lock" title={t('logbook.forbidden')} body={t('logbook.forbiddenBody')} />
      ) : entry.isPending ? (
        <SkeletonRows count={4} />
      ) : entry.isError ? (
        <ErrorState error={entry.error} title={t('logbook.detail.loadFailed')} onRetry={() => entry.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          <EntryHeader entry={entry.data} />

          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
              <Panel>
                <Eyebrow as="h2" className="mb-2">
                  {t('logbook.detail.observation')}
                </Eyebrow>
                <p className="m-0 text-body whitespace-pre-wrap">{entry.data.observation}</p>
              </Panel>

              <Photos photos={entry.data.photos} />
            </div>

            <div className="flex min-w-0 flex-col gap-4">
              <Linked entry={entry.data} />
              <SignalWindows tags={entry.data.sensorTags} />
            </div>
          </div>
        </div>
      )}

      {editing && entry.data ? (
        <LogbookFormDialog
          /* Mounted only while open, so it seeds afresh from the detail — which
             `staleTime: 0` and the stream keep current. The shift is locked on
             an edit, so the dialog needs no Shift Master list: it names the
             entry's own shift from its brief. */
          open
          entry={entry.data}
          shifts={[]}
          shiftsFailed={false}
          canPickAnomaly={canPickAnomaly}
          canPickWorkOrder={canPickWorkOrder}
          canReadSensors={canReadSensors}
          pending={update.isPending}
          onClose={closeForm}
          onSubmit={(payload) =>
            update.mutate(payload, {
              onSuccess: () => {
                closeForm()
                toast(t('logbook.toast.updated'))
              },
              /* A `record_entries` holder editing somebody else's entry is the
                 403 this client cannot predict — said in words, form left open. */
              onError: (error) =>
                toast(
                  error?.code === ERROR_CODES.FORBIDDEN ? t('logbook.toast.updateForbidden') : t('logbook.toast.updateFailed'),
                  'error',
                ),
            })
          }
        />
      ) : null}
    </Page>
  )
}

/* ══ HEADER ════════════════════════════════════════════════════════════ */

/**
 * The record, stated once. Same anatomy as the feed card — badges, title, the
 * place — so arriving reads as a zoom rather than a different screen, with the
 * facts the card had no room for in a side column.
 */
function EntryHeader({ entry }) {
  const { t } = useTranslation(['app', 'common'])
  const stripe = hueForSeverity(entry.severity)
  /* Not `!==`: an unedited row's two stamps differ by microseconds (they are
     two `now()` calls on insert), so only a gap of more than a minute counts. */
  const edited =
    Boolean(entry.updatedAt && entry.createdAt) &&
    new Date(entry.updatedAt).getTime() - new Date(entry.createdAt).getTime() > 60_000

  return (
    <Panel className={cn('flex items-start gap-6 max-lg:flex-wrap', stripe && STRIPE_CLASS[stripe])}>
      <div className="min-w-0 flex-1">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SeverityBadge severity={entry.severity} dot />
          <PlainBadge>{t(`logbook.category.${entry.category}`)}</PlainBadge>
        </div>

        <h1 className="m-0 text-headline">{entry.title}</h1>
        <HierarchyTrail hierarchy={entry.hierarchy} className="mt-2" />
      </div>

      <div className="shrink-0 max-lg:w-full">
        <Serial as="div" value={entry.serialNumber} className="mb-2.5 font-semibold text-on-forge lg:text-end" />

        <dl className="m-0 flex flex-wrap items-start gap-x-8 gap-y-3">
          <Fact label={t('logbook.detail.shift')}>
            {entry.shift?.name ?? t('logbook.detail.unknownShift')}
            {entry.shift && !entry.shift.active ? (
              <span className="ms-1.5 text-ink-dim">{t('logbook.detail.shiftInactive')}</span>
            ) : null}
          </Fact>

          {/* The SHIFT DATE — a calendar day, so `formatDay`, which does not
              shift it across midnight the way an instant formatter would. */}
          <Fact label={t('logbook.detail.shiftDate')}>
            <Num>{formatDay(entry.shiftDate)}</Num>
          </Fact>

          <Fact label={t('logbook.detail.written')}>
            <Num>{formatDateTime(entry.createdAt)}</Num>
            {edited ? (
              <span className="block text-micro text-ink-dim">
                <Num>{t('logbook.detail.edited', { when: formatDateTime(entry.updatedAt) })}</Num>
              </span>
            ) : null}
          </Fact>
        </dl>
      </div>
    </Panel>
  )
}

function Fact({ label, children }) {
  return (
    <div>
      <Eyebrow as="dt">{label}</Eyebrow>
      <dd className="m-0 mt-1.5 text-body-sm font-medium">{children}</dd>
    </div>
  )
}

/* ══ BODY ══════════════════════════════════════════════════════════════ */

/**
 * The anomaly and the work order the writer linked, from the `*_detail` briefs
 * — which is why they read correctly for someone without either module's
 * grant. Linked by the `*_id` beside them; the brief carries no id. A link
 * whose target was deleted serves the id with no brief, and says so.
 */
function Linked({ entry }) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <Panel>
      <Eyebrow as="h2" className="mb-3">
        {t('logbook.detail.linked')}
      </Eyebrow>

      {!entry.anomalyId && !entry.workOrderId ? (
        <p className="m-0 text-body-sm text-ink-dim">{t('logbook.detail.noneLinked')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {entry.anomalyId ? (
            <LinkedItem
              icon="bolt"
              kind={t('logbook.entry.linkedAnomaly')}
              to={`/app/anomalies/${entry.anomalyId}`}
              brief={entry.anomaly}
              meta={
                entry.anomaly
                  ? `${t(`common:severity.${entry.anomaly.severity}`, { defaultValue: entry.anomaly.severity })} · ${statusLabel(t, entry.anomaly.status)}`
                  : null
              }
            />
          ) : null}
          {entry.workOrderId ? (
            <LinkedItem
              icon="wrench"
              kind={t('logbook.entry.linkedWorkOrder')}
              to={`/app/work-orders?wo=${entry.workOrderId}`}
              brief={entry.workOrder}
              meta={
                entry.workOrder
                  ? `${t(`workOrders.priority.${entry.workOrder.priority}`, { defaultValue: entry.workOrder.priority })} · ${t(
                      `workOrders.state.${camel(entry.workOrder.status)}`,
                      { defaultValue: entry.workOrder.status },
                    )}`
                  : null
              }
            />
          ) : null}
        </ul>
      )}
    </Panel>
  )
}

function LinkedItem({ icon, kind, to, brief, meta }) {
  const { t } = useTranslation('app')
  return (
    <li>
      <Link
        to={to}
        className="group flex items-start gap-2.5 rounded-xs p-1.5 -m-1.5 text-ink no-underline transition-colors hover:bg-sunken"
      >
        <Icon name={icon} size="xs" className="mt-1 shrink-0 text-ink-dim" />
        <span className="min-w-0 flex-1">
          <span className="block text-micro text-ink-dim">
            {kind}
            {brief ? (
              <>
                {' · '}
                <Serial value={brief.serialNumber} />
              </>
            ) : null}
          </span>
          <span className="block truncate text-body-sm font-semibold group-hover:text-on-forge">
            {brief?.title ?? t('logbook.detail.linkGone')}
          </span>
          {meta ? <span className="block text-micro text-ink-dim">{meta}</span> : null}
        </span>
        <Icon name="right" size="xs" className="mirror-rtl mt-1 shrink-0 text-ink-dim" />
      </Link>
    </li>
  )
}

/**
 * The photos. Each `viewUrl` is presigned with a short life and is re-resolved
 * on every read and every stream event — which is the other reason the detail
 * query is never cached past its screen.
 */
function Photos({ photos }) {
  const { t } = useTranslation('app')
  if (photos.length === 0) return null

  return (
    <Panel>
      <Eyebrow as="h2" className="mb-3">
        {t('logbook.detail.photos')} <Num className="text-ink-dim">{photos.length}</Num>
      </Eyebrow>
      <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3">
        {photos.map((photo, index) => (
          <li key={photo.fileId}>
            <a
              href={photo.viewUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="block overflow-hidden rounded-xs border border-hairline bg-sunken"
            >
              <img
                src={photo.viewUrl}
                alt={t('logbook.detail.photoAlt', { n: index + 1 })}
                loading="lazy"
                className="aspect-square w-full object-cover"
              />
            </a>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/**
 * The sensor tags pinned to the entry, each with the window the writer chose.
 * Windows are naive UTC on the wire; `fromNaiveUtc`'s rule applies — the `Z` is
 * appended before parsing, or the instant drifts by the reader's offset.
 */
function SignalWindows({ tags }) {
  const { t } = useTranslation('app')
  if (tags.length === 0) return null

  return (
    <Panel>
      <Eyebrow as="h2" className="mb-3">
        {t('logbook.detail.signals')} <Num className="text-ink-dim">{tags.length}</Num>
      </Eyebrow>
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {tags.map((tag) => (
          <li key={tag.id} className="text-body-sm">
            <div className="flex items-center gap-2">
              <Icon name="wave" size="xs" className="shrink-0 text-ink-dim" />
              <span className="min-w-0 flex-1 truncate font-semibold">{tag.name || '—'}</span>
              {!tag.active ? <span className="text-micro text-ink-dim">{t('logbook.detail.signalPaused')}</span> : null}
            </div>
            <p className="m-0 mt-0.5 ps-5 text-micro text-ink-dim">
              <Num>
                {formatDateTime(utc(tag.windowStart))} – {formatDateTime(utc(tag.windowEnd))}
              </Num>
            </p>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/** Naive UTC → an instant. See `fromNaiveUtc` in the feature module. */
const utc = (value) => (value ? `${String(value).replace(' ', 'T').replace(/Z$/, '')}Z` : null)

/** `in_progress` → `inProgress`, the app's spelling of `WorkOrderStatus`. */
const camel = (value) => String(value ?? '').replace(/_([a-z])/g, (_, c) => c.toUpperCase())
